import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../components/AppIcon";
import Logo from "./components/Logo";
import FormInput from "./components/FormInput";
import Checkbox from "./components/Checkbox";
import ActionButton from "./components/ActionButton";
import PasswordResetModal from "./components/PasswordResetModal";
import ErrorMessage from "./components/ErrorMessage";
import useUserStore from "components/storeUserData";
const apiUrl = import.meta.env.VITE_API_URL;

const LoginScreen = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [loginError, setLoginError] = useState("");
  const setUserData = useUserStore(state => state.setUserData);
  // const userData = useUserStore(state => state.userData);
  // const clearUserData = useUserStore(state => state.clearUserData);

  // Mock credentials for demonstration
  const mockCredentials = {
    email: "admin@nextadmin.com",
    password: "Admin@123",
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }

    // Clear login error when user modifies form
    if (loginError) {
      setLoginError("");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email address is invalid";
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      // Simulate demo fetch API call

      const response = await fetch(`${apiUrl}/api/admin-user/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setUserData(data.res);
        navigate('/dashboard-overview');
      } else {
        setLoginError("Invalid email or password. Please try again.");
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setLoginError("Invalid email or password. Please try again.");
    }
  };

  const openPasswordResetModal = (e) => {
    e.preventDefault();
    setShowPasswordResetModal(true);
  };

  const closePasswordResetModal = () => {
    setShowPasswordResetModal(false);
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-background">
      {/* Left side - Branding */}
      <div className="hidden md:flex md:w-1/2 bg-primary-700 text-white p-8 flex-col justify-between">
        <div className="max-w-md mx-auto mt-16">
          {/* Logo */}
          <div className="mb-8">
            <Logo variant="light" />
          </div>

          {/* Title & Subtitle */}
          <h1 className="text-4xl font-bold mb-6">Welcome to EternitAI</h1>
          <p className="text-xl text-primary-100 mb-8">
            Transform your ideas into stunning visuals with AI-powered prompt-to-image generation.
          </p>

          {/* Feature Highlights */}
          <div className="bg-primary-600 bg-opacity-30 p-6 rounded-lg border border-primary-500 border-opacity-30 backdrop-blur-sm">
            <div className="flex items-start mb-4">
              <div className="mr-3 mt-1">
                <Icon name="CheckCircle" className="text-primary-300" />
              </div>
              <p className="text-primary-100">Generate high-quality images using simple text prompts</p>
            </div>
            <div className="flex items-start mb-4">
              <div className="mr-3 mt-1">
                <Icon name="CheckCircle" className="text-primary-300" />
              </div>
              <p className="text-primary-100">Customize, upscale, and fine-tune your visual content</p>
            </div>
            <div className="flex items-start">
              <div className="mr-3 mt-1">
                <Icon name="CheckCircle" className="text-primary-300" />
              </div>
              <p className="text-primary-100">Seamlessly export and use images across your projects</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-primary-200 text-sm">
          &copy; {new Date().getFullYear()} EternitAI. All rights reserved.
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8">
            <Logo variant="dark" />
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Sign in to your account</h2>
            <p className="text-gray-600">Enter your credentials to access your dashboard</p>
          </div>

          {loginError && (
            <ErrorMessage
              message={loginError}
              onClose={() => setLoginError("")}
            />
          )}

          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            <FormInput
              label="Email address"
              type="email"
              name="email"
              id="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="admin@gmail.com"
              error={errors.email}
              autoComplete="email"
              required
              aria-required="true"
              aria-invalid={errors.email ? "true" : "false"}
            />

            <FormInput
              label="Password"
              type="password"
              name="password"
              id="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••••"
              error={errors.password}
              autoComplete="current-password"
              required
              aria-required="true"
              aria-invalid={errors.password ? "true" : "false"}
            />

            <div className="flex items-center justify-between">
              <Checkbox
                id="rememberMe"
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={handleChange}
                label="Remember me"
              />
              {/* <button
                type="button"
                onClick={openPasswordResetModal}
                className="text-sm font-medium text-primary-600 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                Forgot password?
              </button> */}
            </div>

            <ActionButton
              type="submit"
              fullWidth
              isLoading={isLoading}
              disabled={isLoading}
            >
              Sign in
            </ActionButton>
          </form>

          {/* Mobile footer */}
          <div className="mt-8 text-center text-sm text-gray-500 md:hidden">
            &copy; {new Date().getFullYear()} EternitAI. All rights reserved.
          </div>
        </div>
      </div>

      {/* Password Reset Modal */}
      {showPasswordResetModal && (
        <PasswordResetModal onClose={closePasswordResetModal} />
      )}
    </div>
  );
};

export default LoginScreen;